export const ACTION_HUB_ROOT_CLASS = 'action-hub-root'
export const MAX_FEEDBACK_CHARACTERS = 1000

/** Body classes that indicate an overlay is open — Action Hub FAB should hide. */
export const ACTION_HUB_OVERLAY_BODY_CLASSES = {
  SF_CHAT: 'sf-chat-open',
  FEEDBACK: 'action-hub-feedback-open',
  PEARY: 'peary-chat-open',
}

export const HELP_CENTER_URL =
  'https://docs.goguardian.com/pear-deck/pear-assessment'

export const ACTION_IDS = {
  SUPPORT: 'support',
  PEARY: 'peary',
  FEEDBACK: 'feedback',
  ARTICLES: 'articles',
}

export const getFeedbackCharacterCount = (text = '') => text.trim().length

export const trimToCharacterLimit = (
  text = '',
  limit = MAX_FEEDBACK_CHARACTERS
) => {
  const trimmed = text.trim()
  if (!trimmed) return trimmed

  if (trimmed.length <= limit) return trimmed

  return trimmed.slice(0, limit)
}

export const hideSalesforceChatButtonApi = () => {
  if (window.embeddedservice_bootstrap?.utilAPI?.hideChatButton) {
    try {
      window.embeddedservice_bootstrap.utilAPI.hideChatButton()
      return true
    } catch {
      // hideChatButton throws when the chat window is already open — expected.
    }
  }
  return false
}

export const hideSalesforceChatBubble = () => {
  const styleId = 'action-hub-sf-bubble-hide'
  if (document.getElementById(styleId)) return

  const style = document.createElement('style')
  style.id = styleId
  style.textContent = `
    .embeddedMessagingConversationButtonWrapper,
    .embeddedMessagingConversationButton,
    .embeddedMessagingConversationButtonWrapper iframe,
    .embeddedServiceHelpButton,
    .embeddedServiceHelpButton .helpButtonEnabled {
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      overflow: hidden !important;
      pointer-events: none !important;
      visibility: hidden !important;
    }
  `
  document.head.appendChild(style)
}

/** Hide the default SF launcher via API (when available) and CSS fallback. */
export const hideSalesforceChatLauncher = () => {
  hideSalesforceChatButtonApi()
  hideSalesforceChatBubble()
}

const SF_BUTTON_REHIDE_EVENTS = [
  'onEmbeddedMessagingButtonCreated',
  'onEmbeddedMessagingReady',
  'onEmbeddedMessagingWindowMinimized',
  'onEmbeddedMessagingWindowClosed',
  'onEmbeddedMessagingConversationClosed',
]

let sfButtonHidingSetup = false

/**
 * Keep the default Salesforce launcher hidden; Action Hub opens chat via launchChat.
 * Always hides on call; registers SF re-hide listeners only once.
 * Re-applies hiding when SF recreates the button (e.g. after minimize).
 */
export const setupSalesforceChatButtonHiding = () => {
  hideSalesforceChatLauncher()

  if (sfButtonHidingSetup) return () => {}
  sfButtonHidingSetup = true

  const handleHide = () => {
    hideSalesforceChatLauncher()
  }

  SF_BUTTON_REHIDE_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleHide)
  })

  return () => {
    SF_BUTTON_REHIDE_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleHide)
    })
    sfButtonHidingSetup = false
  }
}

export const openSalesforceChat = () => {
  if (window.embeddedservice_bootstrap?.utilAPI?.launchChat) {
    window.embeddedservice_bootstrap.utilAPI.launchChat()
    return true
  }

  const button = document.querySelector('.embeddedMessagingConversationButton')
  if (button) {
    button.click()
    return true
  }
  const legacyButton = document.querySelector(
    '.embeddedServiceHelpButton .helpButtonEnabled'
  )
  if (legacyButton) {
    legacyButton.click()
    return true
  }
  return false
}

export const openChatMaintenanceNotification = () => {
  const maintenanceIcon = document.getElementById('chat-maintenance-icon')
  if (maintenanceIcon) {
    maintenanceIcon.click()
    return true
  }
  return false
}

/** Hide the maintenance-mode chat FAB; Support in Action Hub opens it programmatically. */
export const hideChatMaintenanceIcon = () => {
  const styleId = 'action-hub-chat-maintenance-hide'
  if (document.getElementById(styleId)) return

  const style = document.createElement('style')
  style.id = styleId
  style.textContent = `
    #chat-maintenance-icon {
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      overflow: hidden !important;
      pointer-events: none !important;
    }
  `
  document.head.appendChild(style)
}

const isElementVisible = (element) => {
  if (!element) return false
  if (element.getAttribute('aria-hidden') === 'true') return false
  const { display, visibility, opacity } = window.getComputedStyle(element)
  if (display === 'none' || visibility === 'hidden') return false
  if (Number(opacity) === 0) return false
  const { width, height } = element.getBoundingClientRect()
  return width > 0 && height > 0
}

const isLegacySalesforceChatVisible = () => {
  const sidebar = document.querySelector('.embeddedServiceSidebar')
  return isElementVisible(sidebar)
}

/** Enhanced Messaging window is much larger than the hidden launcher bubble. */
export const isEnhancedSalesforceChatVisible = () => {
  const openWindowSelectors = [
    '.embeddedMessagingFrame',
    '.embeddedMessagingWindow',
    '.embeddedMessagingModalContainer',
  ]

  if (
    openWindowSelectors.some((selector) =>
      isElementVisible(document.querySelector(selector))
    )
  ) {
    return true
  }

  const wrapper = document.querySelector(
    '.embeddedMessagingConversationButtonWrapper'
  )
  if (wrapper) {
    const iframe = wrapper.querySelector('iframe')
    if (iframe) {
      const { width, height } = iframe.getBoundingClientRect()
      if (width > 100 && height > 100 && isElementVisible(iframe)) {
        return true
      }
    }
  }

  return false
}

export const isSalesforceChatVisible = () =>
  isLegacySalesforceChatVisible() || isEnhancedSalesforceChatVisible()

export const setSalesforceChatOpen = (isOpen) => {
  document.body.classList.toggle(
    ACTION_HUB_OVERLAY_BODY_CLASSES.SF_CHAT,
    isOpen
  )
}

export const injectActionHubOverlayHideStyles = () => {
  const styleId = 'action-hub-overlay-hide'
  if (document.getElementById(styleId)) return

  const { SF_CHAT, FEEDBACK, PEARY } = ACTION_HUB_OVERLAY_BODY_CLASSES
  const style = document.createElement('style')
  style.id = styleId
  style.textContent = `
    body.${PEARY} .${ACTION_HUB_ROOT_CLASS},
    body.${SF_CHAT} .${ACTION_HUB_ROOT_CLASS},
    body.${FEEDBACK} .${ACTION_HUB_ROOT_CLASS} {
      display: none !important;
    }
  `
  document.head.appendChild(style)
}

const SF_CLOSE_INTENT_MS = 500
/** Keep Action Hub hidden while End Chat tears down the still-open SF window. */
const SF_END_CHAT_CLOSE_INTENT_MS = 5000
const SF_DOM_VISIBILITY_DEBOUNCE_MS = 300

const restoreActionHubVisibility = () => {
  setSalesforceChatOpen(false)
  const actionHubRoot = document.querySelector(`.${ACTION_HUB_ROOT_CLASS}`)
  if (actionHubRoot) {
    actionHubRoot.style.removeProperty('display')
  }
}

/**
 * Clears the Salesforce Embedded Messaging session and closes the chat window.
 * Used on End Chat (Action Hub), logout, and fresh login.
 * @see https://developer.salesforce.com/docs/service/messaging-web/references/m4w-reference/userVerificationAPI.html
 */
export const clearSalesforceChatSession = (shouldEndSession = true) => {
  const api = window.embeddedservice_bootstrap?.userVerificationAPI
  if (!api?.clearSession) {
    hideSalesforceChatLauncher()
    return Promise.resolve(false)
  }
  return api
    .clearSession(shouldEndSession)
    .then(() => true)
    .catch(() => false)
    .finally(hideSalesforceChatLauncher)
}

/**
 * Subscribe to Salesforce chat open/close. Returns an unsubscribe function.
 * onOpen/onClose are called when chat visibility changes.
 */
export const subscribeSalesforceChatVisibility = ({ onOpen, onClose } = {}) => {
  let sfChatOpen = false
  let closeDebounceTimer = null
  let closeIntentUntil = 0

  const clearCloseDebounce = () => {
    if (closeDebounceTimer) {
      window.clearTimeout(closeDebounceTimer)
      closeDebounceTimer = null
    }
  }

  const syncOpenState = (nextOpen) => {
    if (nextOpen === sfChatOpen) return
    sfChatOpen = nextOpen
    if (nextOpen) {
      clearCloseDebounce()
      closeIntentUntil = 0
      setSalesforceChatOpen(true)
      onOpen?.()
      return
    }
    restoreActionHubVisibility()
    onClose?.()
  }

  const forceOpen = () => syncOpenState(true)
  const markOpen = () => {
    if (Date.now() < closeIntentUntil) return
    forceOpen()
  }
  const markClosed = () => syncOpenState(false)

  const handleSfClose = () => {
    closeIntentUntil = Date.now() + SF_CLOSE_INTENT_MS
    markClosed()
    clearCloseDebounce()
    closeDebounceTimer = window.setTimeout(() => {
      markClosed()
      closeIntentUntil = 0
      closeDebounceTimer = null
    }, SF_CLOSE_INTENT_MS)
  }

  /** End Chat leaves SF window open — clear session so Action Hub "?" restores. */
  const handleConversationClosed = () => {
    closeIntentUntil = Date.now() + SF_END_CHAT_CLOSE_INTENT_MS
    clearSalesforceChatSession().finally(handleSfClose)
  }

  const sfOpenEvents = [
    'onEmbeddedMessagingConversationOpened',
    'onEmbeddedMessagingWindowMaximized',
  ]

  sfOpenEvents.forEach((eventName) => {
    window.addEventListener(eventName, forceOpen)
  })
  window.addEventListener('onEmbeddedMessagingWindowClosed', handleSfClose)
  window.addEventListener(
    'onEmbeddedMessagingConversationClosed',
    handleConversationClosed
  )

  const handleMinimized = () => {
    if (Date.now() < closeIntentUntil) return
    if (isSalesforceChatVisible()) {
      markOpen()
      return
    }
    closeIntentUntil = 0
    markClosed()
  }
  window.addEventListener('onEmbeddedMessagingWindowMinimized', handleMinimized)

  const syncFromDom = () => {
    if (Date.now() < closeIntentUntil) {
      // During End Chat teardown the SF window is still visible — keep FAB hidden.
      return
    }

    if (isSalesforceChatVisible()) {
      markOpen()
      return
    }

    if (!sfChatOpen) return

    clearCloseDebounce()
    closeDebounceTimer = window.setTimeout(() => {
      if (Date.now() < closeIntentUntil || !isSalesforceChatVisible()) {
        markClosed()
      }
      closeDebounceTimer = null
    }, SF_DOM_VISIBILITY_DEBOUNCE_MS)
  }

  const observer = new MutationObserver(syncFromDom)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
  })

  return () => {
    sfOpenEvents.forEach((eventName) => {
      window.removeEventListener(eventName, forceOpen)
    })
    window.removeEventListener('onEmbeddedMessagingWindowClosed', handleSfClose)
    window.removeEventListener(
      'onEmbeddedMessagingConversationClosed',
      handleConversationClosed
    )
    window.removeEventListener(
      'onEmbeddedMessagingWindowMinimized',
      handleMinimized
    )
    clearCloseDebounce()
    closeIntentUntil = 0
    observer.disconnect()
    markClosed()
  }
}

/** @deprecated Use subscribeSalesforceChatVisibility */
export const setupSalesforceChatActionHubSync = () =>
  subscribeSalesforceChatVisibility()
